import { c, card, tbl } from '../../theme';
import { fmtChange, fmtCount, fmtMmSs, fmtMoney, fmtPct, STATUS, statusColor } from './format';

// Every pod over the window, busiest first, then the whole support center.
//
// The "All pods" Handled is the organization's own count — each conversation
// once — so it is LESS than the column above it when conversations touched
// more than one pod. The footnote says so, because a total that does not add
// up and does not explain itself reads as an error. Service level and AHT in
// that row are every pod's numerators over every pod's denominators.
export default function LeadershipPodTable({ pods, allPods, rangeLabel }) {
  const cell = (status, text) => (
    <td style={{ ...s.num, color: statusColor(status), fontWeight: status ? 600 : 400 }}>{text}</td>
  );
  const row = (label, r, strong) => (
    <tr key={label} style={strong ? s.totalRow : undefined}>
      <td style={{ ...s.td, fontWeight: strong ? 700 : 500 }}>{label}</td>
      <td style={s.num}>{fmtCount(r.handled)}{r.handledComplete === false ? '*' : ''}</td>
      <td style={s.num} title={r.forecastCoveragePct ? `Forecast covers ${r.forecastCoveragePct}% of the days` : 'No forecast for this pod'}>
        {fmtChange(r.forecastVsPct, '%')}
      </td>
      {cell(STATUS.serviceLevel(r.serviceLevel), fmtPct(r.serviceLevel))}
      {cell(STATUS.aht(r.aht), fmtMmSs(r.aht))}
      <td style={s.num}>{fmtMmSs(r.asa)}</td>
      {cell(STATUS.abandonPct(r.abandonPct), fmtPct(r.abandonPct))}
      <td style={s.num}>{fmtMoney(r.costPerCall)}</td>
    </tr>
  );
  const incomplete = [...pods, allPods].some((r) => r.handledComplete === false);

  return (
    <section style={s.card} aria-label="Pod performance">
      <div style={s.head}>
        <h2 style={s.h2}>Pod performance · {rangeLabel}</h2>
        <span style={s.legend}>Green on target · amber near · red off</span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Pod</th>
              <th style={s.thNum}>Handled</th>
              <th style={s.thNum}>vs Forecast</th>
              <th style={s.thNum}>Service level</th>
              <th style={s.thNum}>AHT</th>
              <th style={s.thNum}>Speed of answer</th>
              <th style={s.thNum}>Abandon rate</th>
              <th style={s.thNum}>Cost / call*</th>
            </tr>
          </thead>
          <tbody>
            {pods.map((p) => row(p.pod, p, false))}
            {row('All pods', allPods, true)}
          </tbody>
        </table>
      </div>
      <p style={s.foot}>
        Handled counts each conversation once. &ldquo;All pods&rdquo; is the support center&rsquo;s own
        count, so it is lower than the pods added together when a conversation reached more than one pod.
        Service level, AHT and speed of answer are Voice and VIP; abandon rate is Voice and VIP abandons
        over Voice and VIP offered. vs Forecast compares Handled with the uploaded forecast on the days the
        forecast covers; &mdash; means no forecast (IRG has none). *Cost per call is modeled from AHT and the
        platform cost, not a Finance actual.
        {incomplete && ' Some days in this window have no Handled count stored yet (marked *).'}
      </p>
    </section>
  );
}

const s = {
  card:     { ...card.base, padding: '16px 18px' },
  head:     { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap', marginBottom: '10px' },
  h2:       { fontSize: '13px', fontWeight: 650, color: c.text, margin: 0 },
  legend:   { fontSize: '11px', color: c.textMuted },
  table:    { width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' },
  th:       { ...tbl.th, textAlign: 'left' },
  thNum:    { ...tbl.thR, whiteSpace: 'nowrap' },
  td:       { ...tbl.td, color: c.textStrong },
  num:      { ...tbl.tdR, color: c.textStrong },
  totalRow: { background: c.surfaceSunk, borderTop: `2px solid ${c.border}` },
  foot:     { fontSize: '11px', color: c.textMuted, lineHeight: 1.5, margin: '10px 0 0', maxWidth: '80ch' },
};
